import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Activity, 
  Mail, 
  MessageSquare, 
  TrendingUp,
  Download,
  Trash2,
  Eye,
  LogOut,
  Search,
  CheckCircle,
  Clock,
  BarChart3,
  LineChart as LineChartIcon
} from 'lucide-react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, title, value, subtitle, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100"
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-slate-500 text-sm font-medium">{title}</p>
        <p className="text-3xl font-bold text-slate-900 mt-1">{value}</p>
        {subtitle && <p className="text-slate-400 text-sm mt-1">{subtitle}</p>}
      </div>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </motion.div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
        <p className="text-slate-600 text-sm font-medium mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: <span className="font-semibold">{entry.value}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [chartType, setChartType] = useState('area'); // area, line, bar

  const token = localStorage.getItem('adminToken');

  const axiosAuth = axios.create({
    headers: { Authorization: `Bearer ${token}` }
  });

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [token, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, contactsRes, subscribersRes, analyticsRes] = await Promise.all([
        axiosAuth.get(`${API}/admin/stats`),
        axiosAuth.get(`${API}/admin/contacts`),
        axiosAuth.get(`${API}/admin/subscribers`),
        axiosAuth.get(`${API}/admin/analytics?days=14`),
      ]);
      setStats(statsRes.data);
      setContacts(contactsRes.data);
      setSubscribers(subscribersRes.data);
      setAnalytics(analyticsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
  };

  const handleMarkRead = async (contactId) => {
    try {
      await axiosAuth.put(`${API}/admin/contacts/${contactId}/read`);
      setContacts(contacts.map(c => c.id === contactId ? { ...c, read: true } : c));
      setStats({ ...stats, unread_contacts: stats.unread_contacts - 1 });
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    try {
      await axiosAuth.delete(`${API}/admin/contacts/${contactId}`);
      setContacts(contacts.filter(c => c.id !== contactId));
      setStats({ ...stats, total_contacts: stats.total_contacts - 1 });
    } catch (err) {
      console.error('Error deleting contact:', err);
    }
  };

  const handleDeleteSubscriber = async (subscriberId) => {
    if (!window.confirm('Are you sure you want to delete this subscriber?')) return;
    try {
      await axiosAuth.delete(`${API}/admin/subscribers/${subscriberId}`);
      setSubscribers(subscribers.filter(s => s.id !== subscriberId));
      setStats({ ...stats, total_subscribers: stats.total_subscribers - 1 });
    } catch (err) {
      console.error('Error deleting subscriber:', err);
    }
  };

  const handleExport = async (type) => {
    try {
      const response = await axiosAuth.get(`${API}/admin/export/${type}`);
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_export_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting:', err);
    }
  };

  const filteredContacts = contacts.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredSubscribers = subscribers.filter(s =>
    s.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Format chart data
  const chartData = analytics?.daily_data?.map(item => ({
    date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    Contacts: item.contacts,
    Subscribers: item.subscribers,
  })) || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                <Activity className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="font-heading font-bold text-lg text-slate-900">CureClave</span>
                <span className="text-slate-400 text-sm ml-2">Admin</span>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-slate-600 hover:text-red-600"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-5 h-5 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 bg-white p-1 rounded-xl shadow-sm border border-slate-100 w-fit">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'analytics', label: 'Analytics', icon: LineChartIcon },
            { id: 'contacts', label: 'Contacts', icon: MessageSquare },
            { id: 'subscribers', label: 'Subscribers', icon: Mail },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                activeTab === tab.id
                  ? 'bg-cyan-500 text-white'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
              data-testid={`admin-tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div className="space-y-8">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                icon={MessageSquare}
                title="Total Contacts"
                value={stats.total_contacts}
                subtitle={`${stats.contacts_today} today`}
                color="bg-blue-500"
              />
              <StatCard
                icon={Mail}
                title="Subscribers"
                value={stats.total_subscribers}
                subtitle={`${stats.subscribers_today} today`}
                color="bg-emerald-500"
              />
              <StatCard
                icon={Clock}
                title="Unread Messages"
                value={stats.unread_contacts}
                subtitle="Needs attention"
                color="bg-amber-500"
              />
              <StatCard
                icon={TrendingUp}
                title="This Week"
                value={stats.contacts_this_week + stats.subscribers_this_week}
                subtitle="New interactions"
                color="bg-purple-500"
              />
            </div>

            {/* Mini Chart */}
            {chartData.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading font-semibold text-lg text-slate-900">Activity Overview</h3>
                  <span className="text-sm text-slate-500">Last 14 days</span>
                </div>
                <div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="colorContacts" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#06B6D4" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#06B6D4" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorSubscribers" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#64748B' }} />
                      <YAxis tick={{ fontSize: 12, fill: '#64748B' }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="Contacts" stroke="#06B6D4" fillOpacity={1} fill="url(#colorContacts)" strokeWidth={2} />
                      <Area type="monotone" dataKey="Subscribers" stroke="#10B981" fillOpacity={1} fill="url(#colorSubscribers)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
              <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">Quick Actions</h3>
              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={() => handleExport('contacts')}
                  variant="outline"
                  className="rounded-xl"
                  data-testid="export-contacts-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export Contacts
                </Button>
                <Button
                  onClick={() => handleExport('subscribers')}
                  variant="outline"
                  className="rounded-xl"
                  data-testid="export-subscribers-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export Subscribers
                </Button>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">Recent Contacts</h3>
                <div className="space-y-3">
                  {contacts.slice(0, 5).map((contact) => (
                    <div key={contact.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                      <div>
                        <p className="font-medium text-slate-900">{contact.name}</p>
                        <p className="text-sm text-slate-500">{contact.email}</p>
                      </div>
                      {!contact.read && (
                        <span className="px-2 py-1 bg-cyan-100 text-cyan-700 text-xs font-medium rounded-full">New</span>
                      )}
                    </div>
                  ))}
                  {contacts.length === 0 && (
                    <p className="text-slate-500 text-center py-4">No contacts yet</p>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">Recent Subscribers</h3>
                <div className="space-y-3">
                  {subscribers.slice(0, 5).map((sub) => (
                    <div key={sub.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                      <p className="font-medium text-slate-900">{sub.email}</p>
                      <span className="text-sm text-slate-500">
                        {new Date(sub.subscribed_at).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                  {subscribers.length === 0 && (
                    <p className="text-slate-500 text-center py-4">No subscribers yet</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && analytics && (
          <div className="space-y-6">
            {/* Chart Type Selector */}
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-semibold text-slate-900">Analytics Dashboard</h2>
              <div className="flex gap-2 bg-white p-1 rounded-lg border border-slate-200">
                {[
                  { id: 'area', label: 'Area' },
                  { id: 'line', label: 'Line' },
                  { id: 'bar', label: 'Bar' },
                ].map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setChartType(type.id)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                      chartType === type.id
                        ? 'bg-cyan-500 text-white'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid={`chart-type-${type.id}`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Main Chart */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="font-heading font-semibold text-lg text-slate-900">Activity Trends</h3>
                  <p className="text-sm text-slate-500">Contacts and subscribers over the last 14 days</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-cyan-500"></div>
                    <span className="text-sm text-slate-600">Contacts</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                    <span className="text-sm text-slate-600">Subscribers</span>
                  </div>
                </div>
              </div>
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  {chartType === 'area' ? (
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="colorContactsFull" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#06B6D4" stopOpacity={0.4}/>
                          <stop offset="95%" stopColor="#06B6D4" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorSubscribersFull" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10B981" stopOpacity={0.4}/>
                          <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#64748B' }} />
                      <YAxis tick={{ fontSize: 12, fill: '#64748B' }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Area type="monotone" dataKey="Contacts" stroke="#06B6D4" fillOpacity={1} fill="url(#colorContactsFull)" strokeWidth={2} />
                      <Area type="monotone" dataKey="Subscribers" stroke="#10B981" fillOpacity={1} fill="url(#colorSubscribersFull)" strokeWidth={2} />
                    </AreaChart>
                  ) : chartType === 'line' ? (
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#64748B' }} />
                      <YAxis tick={{ fontSize: 12, fill: '#64748B' }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Line type="monotone" dataKey="Contacts" stroke="#06B6D4" strokeWidth={3} dot={{ fill: '#06B6D4', strokeWidth: 2 }} />
                      <Line type="monotone" dataKey="Subscribers" stroke="#10B981" strokeWidth={3} dot={{ fill: '#10B981', strokeWidth: 2 }} />
                    </LineChart>
                  ) : (
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#64748B' }} />
                      <YAxis tick={{ fontSize: 12, fill: '#64748B' }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="Contacts" fill="#06B6D4" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Subscribers" fill="#10B981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </div>
            </div>

            {/* Stats Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-cyan-100 flex items-center justify-center">
                    <MessageSquare className="w-7 h-7 text-cyan-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total Contacts</p>
                    <p className="text-2xl font-bold text-slate-900">{analytics.total_contacts}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center">
                    <Mail className="w-7 h-7 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total Subscribers</p>
                    <p className="text-2xl font-bold text-slate-900">{analytics.total_subscribers}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center">
                    <TrendingUp className="w-7 h-7 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total Interactions</p>
                    <p className="text-2xl font-bold text-slate-900">{analytics.total_contacts + analytics.total_subscribers}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="relative w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="Search contacts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 rounded-xl"
                  data-testid="search-contacts-input"
                />
              </div>
              <Button
                onClick={() => handleExport('contacts')}
                variant="outline"
                className="rounded-xl"
              >
                <Download className="w-4 h-4 mr-2" />
                Export All
              </Button>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Name</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Email</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Message</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Date</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Status</th>
                      <th className="px-6 py-4 text-right text-sm font-semibold text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredContacts.map((contact) => (
                      <tr key={contact.id} className="hover:bg-slate-50">
                        <td className="px-6 py-4">
                          <p className="font-medium text-slate-900">{contact.name}</p>
                        </td>
                        <td className="px-6 py-4">
                          <a href={`mailto:${contact.email}`} className="text-cyan-600 hover:text-cyan-700">
                            {contact.email}
                          </a>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-slate-600 truncate max-w-xs">{contact.message}</p>
                        </td>
                        <td className="px-6 py-4 text-slate-500 text-sm">
                          {new Date(contact.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          {contact.read ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">
                              <CheckCircle className="w-3 h-3" /> Read
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-cyan-100 text-cyan-700 text-xs font-medium rounded-full">
                              <Clock className="w-3 h-3" /> New
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            {!contact.read && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleMarkRead(contact.id)}
                                className="text-cyan-600 hover:text-cyan-700"
                                data-testid={`mark-read-${contact.id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteContact(contact.id)}
                              className="text-red-500 hover:text-red-600"
                              data-testid={`delete-contact-${contact.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredContacts.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    No contacts found
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Subscribers Tab */}
        {activeTab === 'subscribers' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="relative w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="Search subscribers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 rounded-xl"
                  data-testid="search-subscribers-input"
                />
              </div>
              <Button
                onClick={() => handleExport('subscribers')}
                variant="outline"
                className="rounded-xl"
              >
                <Download className="w-4 h-4 mr-2" />
                Export All
              </Button>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Email</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Subscribed On</th>
                      <th className="px-6 py-4 text-right text-sm font-semibold text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredSubscribers.map((sub) => (
                      <tr key={sub.id} className="hover:bg-slate-50">
                        <td className="px-6 py-4">
                          <a href={`mailto:${sub.email}`} className="text-cyan-600 hover:text-cyan-700 font-medium">
                            {sub.email}
                          </a>
                        </td>
                        <td className="px-6 py-4 text-slate-500">
                          {new Date(sub.subscribed_at).toLocaleDateString()} at{' '}
                          {new Date(sub.subscribed_at).toLocaleTimeString()}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteSubscriber(sub.id)}
                              className="text-red-500 hover:text-red-600"
                              data-testid={`delete-subscriber-${sub.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredSubscribers.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    No subscribers found
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
